import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Avatar,
  Fade,
  Grow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import TimelineIcon from "@mui/icons-material/Timeline";
import MapIcon from "@mui/icons-material/Map";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import UserService from "../services/user.service";

const GREEN = {
  900: "#1b5e20",
  800: "#2e7d32",
  700: "#388e3c",
  600: "#43a047",
  500: "#4caf50",
  400: "#66bb6a",
  50: "#e8f5e9",
  bg: "#f8faf9",
  bgAlt: "#f1f5f3",
};

const cardHoverSx = {
  transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1), box-shadow 0.25s cubic-bezier(0.4,0,0.2,1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px ${GREEN[400]}30`,
  },
};

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [content, setContent] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    UserService.getPublicContent().then(
      (response) => setContent(response.data),
      (error) => {
        const _content =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        setContent(_content);
      }
    );
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const { user: currentUser } = useSelector((state) => state.auth);

  const features = [
    {
      title: "Search Pedigree Database",
      description:
        "Explore our comprehensive pedigree database with advanced search capabilities and multiple query options.",
      icon: <SearchIcon sx={{ fontSize: 32, color: GREEN[700] }} />,
      link: "/search",
    },
    {
      title: "Manage Plant Groups",
      description:
        "Create, organize, and manage plant groups efficiently with our intuitive management system.",
      icon: <GroupIcon sx={{ fontSize: 32, color: GREEN[700] }} />,
      link: "/groups",
    },
    {
      title: "Build Radar Charts",
      description:
        "Generate interactive radar charts for comprehensive plant visualization and comparative analysis.",
      icon: <TimelineIcon sx={{ fontSize: 32, color: GREEN[700] }} />,
      link: "/charts",
    },
    {
      title: "Drone Data Visualization",
      description:
        "Visualize and analyze drone-collected field data with advanced mapping and visualization tools.",
      icon: <MapIcon sx={{ fontSize: 32, color: GREEN[700] }} />,
      link: "/drone",
    },
    {
      title: "Phenotype Reports",
      description:
        "Set search parameters to query phenotype data and download CSV exports for offline analysis.",
      icon: <AssessmentIcon sx={{ fontSize: 32, color: GREEN[700] }} />,
      link: "/reports",
    },
  ];

  return (
    <Container
      maxWidth="lg"
      disableGutters
      sx={{
        minHeight: "calc(100vh - 100px)",
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 3 },
      }}
    >
      {/* ── Hero Section ── */}
      <Fade in={visible} timeout={600}>
        <Paper
          elevation={0}
          sx={{
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 7 },
            mb: 4,
            borderRadius: 4,
            background: "#fff",
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: 120,
              height: 4,
              borderRadius: "0 0 4px 4px",
              background: `linear-gradient(90deg, ${GREEN[500]}, ${GREEN[400]})`,
            }}
          />

          <Chip
            label="Burchell Platform"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            size="small"
            sx={{
              mb: 3,
              bgcolor: GREEN[50],
              color: GREEN[800],
              fontWeight: 600,
              fontSize: "0.75rem",
              letterSpacing: 0.5,
              border: `1px solid ${GREEN[400]}30`,
              "& .MuiChip-icon": { color: GREEN[600] },
            }}
          />

          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#1a2e1a",
              mb: 1,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            {currentUser
              ? `Welcome back, ${currentUser.username}`
              : "Burchell Pedigree Platform"}
          </Typography>

          <Box
            sx={{
              width: 48,
              height: 3,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${GREEN[500]}, ${GREEN[400]})`,
              mx: "auto",
              my: 2,
            }}
          />

          <Typography
            variant="body1"
            sx={{
              color: "#546e7a",
              maxWidth: 560,
              mx: "auto",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.7,
            }}
          >
            Advancing agricultural innovation through cutting-edge technology to
            improve human health and food security worldwide.
          </Typography>
        </Paper>
      </Fade>

      {currentUser ? (
        <>
          {/* ── Section Header ── */}
          <Fade in={visible} timeout={1100}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: "#1a2e1a",
                  letterSpacing: "-0.01em",
                }}
              >
                Platform Features
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#78909c", mt: 0.5 }}
              >
                Access the tools you need to manage and analyze your data.
              </Typography>
            </Box>
          </Fade>

          {/* ── Feature Cards ── */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Grow
                  in={visible}
                  timeout={1000 + index * 150}
                  style={{ transformOrigin: "0 0 0" }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "grey.100",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "visible",
                      borderLeft: `3px solid ${GREEN[500]}`,
                      ...cardHoverSx,
                    }}
                  >
                    <CardContent sx={{ flex: 1, p: 3, pb: 1 }}>
                      <Avatar
                        sx={{
                          width: 52,
                          height: 52,
                          bgcolor: GREEN[50],
                          mb: 2.5,
                        }}
                      >
                        {feature.icon}
                      </Avatar>

                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: "#1a2e1a",
                          fontSize: "1.05rem",
                          mb: 1,
                        }}
                      >
                        {feature.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#607d8b", lineHeight: 1.65 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ px: 3, pb: 3, pt: 1 }}>
                      <Button
                        variant="outlined"
                        component={Link}
                        to={feature.link}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          color: GREEN[800],
                          borderColor: GREEN[400],
                          borderRadius: 2,
                          px: 3,
                          py: 0.9,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          "&:visited": {
                            color: GREEN[800],
                          },
                          "&:hover": {
                            borderColor: GREEN[700],
                            color: GREEN[900],
                            bgcolor: GREEN[50],
                          },
                        }}
                      >
                        Explore
                      </Button>
                    </CardActions>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        /* ── Unauthenticated – Simple Login CTA ── */
        <Fade in={visible} timeout={900}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: { xs: 2, md: 4 },
            }}
          >
            <Button
              variant="contained"
              component={Link}
              to="/login"
              endIcon={<LockOpenIcon sx={{ fontSize: 18 }} />}
              disableElevation
              sx={{
                bgcolor: GREEN[600],
                color: "#fff",
                borderRadius: 2.5,
                px: 5,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1.05rem",
                boxShadow: `0 2px 12px ${GREEN[500]}40`,
                "&:hover": {
                  bgcolor: GREEN[800],
                  boxShadow: `0 4px 20px ${GREEN[800]}40`,
                },
              }}
            >
              Continue to Login
            </Button>
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default Home;
